import type { MouseEvent, ReactNode } from 'react';
import { useLayout } from '../state/layout';
import { rememberLook, useVenue, type Look, type ViewMode } from '../state/store';
import { Camera, Clean, More, Plus, Redo, Tape, Undo } from './icons';

/** Undo and redo for every change to the layout. */
export function UndoRedo() {
  const canUndo = useLayout((s) => s.past.length > 0);
  const canRedo = useLayout((s) => s.future.length > 0);
  const undo = useLayout((s) => s.undo);
  const redo = useLayout((s) => s.redo);
  return (
    <div className="history">
      <button type="button" className="icon" title="Undo (Ctrl+Z)" aria-label="Undo" disabled={!canUndo} onClick={undo}>
        <Undo />
      </button>
      <button type="button" className="icon" title="Redo (Ctrl+Shift+Z)" aria-label="Redo" disabled={!canRedo} onClick={redo}>
        <Redo />
      </button>
    </div>
  );
}

function AddButton() {
  const library = useVenue((s) => s.library);
  const set = useVenue((s) => s.set);
  return (
    <button type="button" className="add" aria-label="Add props" aria-pressed={library} aria-expanded={library} onClick={() => set({ library: !library, menuOpen: false, tool: 'none' })} title="Add props">
      <Plus />
      <span>Add</span>
    </button>
  );
}

/** After a click or tap (not a keyboard press), the hall takes the keyboard, so walking keys go to it. */
function focusHall(e: MouseEvent) {
  if (e.detail > 0) document.querySelector<HTMLCanvasElement>('.stage canvas')?.focus({ preventScroll: true });
}

const VIEWS: { id: ViewMode; label: string; key: string }[] = [
  { id: 'plan', label: 'Plan', key: '1' },
  { id: 'orbit', label: 'Orbit', key: '2' },
  { id: 'walk', label: 'Walk', key: '3' },
];

export function ViewSwitch({ onChange }: { onChange: (v: ViewMode) => void }) {
  const view = useVenue((s) => s.view);
  return (
    <div className="seg" role="group" aria-label="How to look at the hall">
      {VIEWS.map((v) => (
        <button
          key={v.id}
          type="button"
          aria-pressed={view === v.id}
          onClick={(e) => {
            onChange(v.id);
            focusHall(e);
          }}
          title={`${v.label} (${v.key})`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

const LOOKS: { id: Look; label: string; title: string }[] = [
  { id: 'scan', label: 'Scan', title: 'The 3D scan of the hall' },
  { id: 'photos', label: 'Photos', title: 'The hall’s measured shape covered with its photos' },
];

/** Scan or Photos, for a hall published with both. */
export function LookSwitch() {
  const look = useVenue((s) => s.look);
  const has = useVenue((s) => !!s.manifest?.shell);
  const set = useVenue((s) => s.set);
  if (!has) return null;
  return (
    <div className="seg look" role="group" aria-label="What the hall is drawn from">
      {LOOKS.map((l) => (
        <button
          key={l.id}
          type="button"
          aria-pressed={look === l.id}
          onClick={(e) => {
            set({ look: l.id });
            rememberLook(l.id);
            focusHall(e);
          }}
          title={l.title}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function Tools({ onScreenshot, busy }: { onScreenshot: () => void; busy: boolean }) {
  const tool = useVenue((s) => s.tool);
  const menuOpen = useVenue((s) => s.menuOpen);
  const set = useVenue((s) => s.set);
  return (
    <div className="tools">
      <button
        type="button"
        className="icon"
        aria-pressed={tool === 'tape'}
        title="Tape measure (T)"
        aria-label="Tape measure"
        onClick={(e) => {
          set({ tool: tool === 'tape' ? 'none' : 'tape', pendingTape: null });
          focusHall(e);
        }}
      >
        <Tape />
      </button>
      <button type="button" className="icon" title="Clean view: hide every panel and label (C)" aria-label="Clean view" onClick={() => set({ clean: true, menuOpen: false })}>
        <Clean />
      </button>
      <button type="button" className="icon" title="Screenshot (P)" aria-label="Take a screenshot" onClick={onScreenshot} disabled={busy}>
        <Camera />
      </button>
      <button type="button" className="icon" aria-pressed={menuOpen} aria-expanded={menuOpen} aria-haspopup="dialog" title="Settings" aria-label="Settings" onClick={() => set({ menuOpen: !menuOpen })}>
        <More />
      </button>
    </div>
  );
}

export function TopBar({ onView, onScreenshot, busy, head, actions, editing }: { onView: (v: ViewMode) => void; onScreenshot: () => void; busy: boolean; head: ReactNode; actions?: ReactNode; editing: boolean }) {
  return (
    <header className="panel topbar">
      <div className="only-laptop head">{head}</div>
      <div className="only-laptop switches">
        <ViewSwitch onChange={onView} />
        <LookSwitch />
      </div>
      <div className="only-laptop right">
        {editing && <UndoRedo />}
        {actions}
        <Tools onScreenshot={onScreenshot} busy={busy} />
        {editing && <AddButton />}
      </div>
      <div className="line1 only-phone">
        {head}
        <Tools onScreenshot={onScreenshot} busy={busy} />
      </div>
      <div className="line2 only-phone">
        <ViewSwitch onChange={onView} />
        <LookSwitch />
        {editing && <UndoRedo />}
        {editing && <AddButton />}
      </div>
    </header>
  );
}
